import { useCallback, useEffect, useState } from 'react';

import { useFilterUrlState } from './useFilterUrlState';
import { INTERVAL_TO_API_MAP, URL_TO_TIME_MAP } from '../constants';
import type { LogLevel, LogType } from '../types';

import type { AsyncAutocompleteSubmitEvent } from '~/components/AsyncAutocomplete';
import type { TimeRange } from '~/components/TimeRangeSelector/TimeRangeSelector';

export function usePipelineLogsFilters() {
  const { filters, setters, clearAllFilters } = useFilterUrlState();
  const [resetToDefault, setResetToDefault] = useState(false);

  const [localPipelineName, setLocalPipelineName] = useState(
    filters.pipelineName
  );
  const [localPipelineVersion, setLocalPipelineVersion] = useState(
    filters.pipelineVersion
  );
  const [localLogMessage, setLocalLogMessage] = useState(filters.logMessage);
  const [localPipelineKey, setLocalPipelineKey] = useState(filters.pipelineKey);

  useEffect(() => {
    setLocalPipelineName(filters.pipelineName);
    setLocalPipelineVersion(filters.pipelineVersion);
    setLocalLogMessage(filters.logMessage);
    setLocalPipelineKey(filters.pipelineKey);
  }, [
    filters.pipelineName,
    filters.pipelineVersion,
    filters.logMessage,
    filters.pipelineKey
  ]);

  const {
    setPipelineName,
    setPipelineVersion,
    setLogMessage,
    setPipelineKey,
    setLevel,
    setLogType,
    setTime,
    setDateFrom,
    setDateTo,
    setDirection
  } = setters;

  const { level, logType, time, dateFrom, dateTo, direction } = filters;

  const handleSearch = useCallback(() => {
    setPipelineName(localPipelineName);
    setPipelineVersion(localPipelineVersion);
    setLogMessage(localLogMessage);
    setPipelineKey(localPipelineKey);

    const internalTimeValue = URL_TO_TIME_MAP[time] || '5m';

    if (internalTimeValue === 'custom-range' && dateFrom && dateTo) {
      setDateFrom(dateFrom);
      setDateTo(dateTo);
    } else {
      const toDateValue = new Date();
      const fromDateValue = new Date();

      switch (internalTimeValue) {
        case '5m':
          fromDateValue.setMinutes(toDateValue.getMinutes() - 5);
          break;
        case '15m':
          fromDateValue.setMinutes(toDateValue.getMinutes() - 15);
          break;
        case '1h':
          fromDateValue.setHours(toDateValue.getHours() - 1);
          break;
        case '1d':
          fromDateValue.setDate(toDateValue.getDate() - 1);
          break;
        default:
          fromDateValue.setMinutes(toDateValue.getMinutes() - 5);
      }

      setDateFrom(fromDateValue.toISOString());
      setDateTo(toDateValue.toISOString());
    }
  }, [
    localPipelineName,
    localPipelineVersion,
    localLogMessage,
    localPipelineKey,
    time,
    dateFrom,
    dateTo,
    setPipelineName,
    setPipelineVersion,
    setLogMessage,
    setPipelineKey,
    setDateFrom,
    setDateTo
  ]);

  const handleTimeRangeChange = useCallback(
    (timeRange: TimeRange) => {
      setResetToDefault(false);

      if (timeRange.name === 'custom-range') {
        setTime(INTERVAL_TO_API_MAP['custom-range']);
        setDateFrom(timeRange.dateFrom);
        setDateTo(timeRange.dateTo);
      } else {
        setTime(
          INTERVAL_TO_API_MAP[
            timeRange.name as keyof typeof INTERVAL_TO_API_MAP
          ] || '5 Minutes'
        );
        setDateFrom(null);
        setDateTo(null);
      }
    },
    [setTime, setDateFrom, setDateTo]
  );

  const handlePipelineNameSubmit = useCallback(
    (event: AsyncAutocompleteSubmitEvent) => {
      const newName = event.type === 'exact' ? event.label : event.value;
      setLocalPipelineName(newName);
      setLocalPipelineVersion('');
      setPipelineName(newName);
      setPipelineVersion('');
    },
    [setPipelineName, setPipelineVersion]
  );

  const handleVersionSubmit = useCallback(
    (event: AsyncAutocompleteSubmitEvent) => {
      const newVersion = event.type === 'exact' ? event.label : event.value;
      setLocalPipelineVersion(newVersion);
      setPipelineVersion(newVersion);
    },
    [setPipelineVersion]
  );

  const handleClear = useCallback(() => {
    setLocalPipelineName('');
    setLocalPipelineVersion('');
    setLocalLogMessage('');
    setLocalPipelineKey('');
    clearAllFilters();
    setResetToDefault(true);
    localStorage.removeItem('pipelineLogsFilters');
  }, [clearAllFilters]);

  return {
    form: {
      values: {
        pipelineName: localPipelineName,
        pipelineVersion: localPipelineVersion,
        logMessage: localLogMessage,
        pipelineKey: localPipelineKey,
        level,
        logType
      },
      setters: {
        setPipelineName: setLocalPipelineName,
        setPipelineVersion: setLocalPipelineVersion,
        setLogMessage: setLocalLogMessage,
        setPipelineKey: setLocalPipelineKey,
        setLevel: (value: LogLevel) => setLevel(value),
        setLogType: (value: LogType) => setLogType(value),
        setDirection
      }
    },
    urlParams: {
      pipelineName: filters.pipelineName || null,
      pipelineVersion: filters.pipelineVersion || null,
      logMessage: filters.logMessage || null,
      pipelineKey: filters.pipelineKey || null,
      level,
      logType,
      dateFrom,
      dateTo,
      direction
    },
    timeState: {
      time,
      dateFrom,
      dateTo,
      resetToDefault
    },
    handleSearch,
    handleTimeRangeChange,
    handleClear,
    handlePipelineNameSubmit,
    handleVersionSubmit
  };
}
